import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { ShoppingCart, Minus, Plus, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast.success(`${quantity} x ${product.name} added to cart!`);
    navigate('/cart');
  };

  if (loading) {
    return (
      <div className="bg-off_white min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-brand_blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="bg-off_white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-chewy text-4xl text-brand_red mb-4">Product Not Found</h2>
          <button
            onClick={() => navigate('/products')}
            className="bg-brand_blue text-white px-6 py-3 rounded-full font-nunito font-bold border-2 border-black shadow-hard"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-off_white min-h-screen py-12" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/products')}
          className="flex items-center gap-2 font-nunito font-bold text-brand_blue hover:text-brand_red mb-8 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={3} />
          Back to Products
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="bg-white border-4 border-black rounded-2xl p-8 shadow-hard">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-auto object-contain"
            />
          </div>

          {/* Product Info */}
          <div>
            <h1 className="font-chewy text-5xl text-brand_blue mb-4">
              {product.name}
            </h1>
            {product.flavor && (
              <p className="font-nunito text-xl text-gray-600 font-semibold mb-4">
                {product.flavor}
              </p>
            )}
            <p className="font-chewy text-4xl text-brand_red mb-6">
              ${product.price.toFixed(2)}
            </p>
            <p className="font-nunito text-lg text-gray-700 mb-8 leading-relaxed">
              {product.description}
            </p>

            {/* Quantity Selector */}
            <div className="mb-8">
              <label className="font-nunito font-bold text-lg mb-2 block">
                Quantity:
              </label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="bg-white border-2 border-black rounded-full w-12 h-12 flex items-center justify-center shadow-hard hover:shadow-hard-hover hover:-translate-y-0.5 transition-all"
                  data-testid="quantity-decrease"
                >
                  <Minus className="w-5 h-5" strokeWidth={3} />
                </button>
                <span className="font-nunito text-2xl font-bold w-12 text-center" data-testid="quantity-value">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="bg-white border-2 border-black rounded-full w-12 h-12 flex items-center justify-center shadow-hard hover:shadow-hard-hover hover:-translate-y-0.5 transition-all"
                  data-testid="quantity-increase"
                >
                  <Plus className="w-5 h-5" strokeWidth={3} />
                </button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              className="w-full bg-brand_blue text-white font-chewy text-2xl px-8 py-4 rounded-full border-4 border-black shadow-hard-lg hover:shadow-hard-hover hover:-translate-y-1 transition-all"
              data-testid="add-to-cart-button"
            >
              <ShoppingCart className="inline-block w-6 h-6 mr-2" strokeWidth={3} />
              ADD TO CART
            </button>

            {/* Additional Info */}
            <div className="mt-8 bg-brand_green/20 border-4 border-black rounded-xl p-6">
              <h3 className="font-chewy text-2xl text-brand_blue mb-4">Product Details</h3>
              <ul className="space-y-2 font-nunito font-semibold">
                <li>✓ 100% All Natural Ingredients</li>
                <li>✓ Made with Blue Agave Nectar</li>
                <li>✓ Fresh Fruit Flavors</li>
                <li>✓ No Artificial Colors or Preservatives</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;